import React, { useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { HiOutlineDocumentMagnifyingGlass } from "react-icons/hi2";

import Input from "@/components/atoms/input/Input";
import InputLabel from "@/components/atoms/input/InputLabel";

export type InfiniteComboBoxProp<T extends { id: string; name: string }> = {
  label?: string;
  options: T[];
  displayValue?: (option: T) => string;
  placeholder?: string;
  renderOption?: (option: T) => React.ReactNode;
  hasError?: boolean;
  leftPlaceholder?: React.ReactNode | string;
  className?: string;
  optionsClassName?: string;
  disabled?: boolean;
  emptyNode?: React.ReactNode;
};

const SelectComboBox = <T extends { id: string; name: string }>({
  label,
  options,
  displayValue = (option) => option?.name,
  placeholder,
  renderOption,
  hasError,
  leftPlaceholder,
  className,
  optionsClassName,
  disabled,
  emptyNode,
}: InfiniteComboBoxProp<T>) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => displayValue(option).toLowerCase().includes(query?.toLowerCase()));

  return (
    <div>
      <div className={className}>
        {label && <InputLabel label={label} className={"mb-1.5"} />}
        <Input
          hasError={hasError}
          leftPlaceholder={leftPlaceholder}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder || t("select.options")}
          disabled={disabled}
        />
      </div>

      <div
        className={clsx(
          "mt-4 max-h-72 w-[350px] max-w-full overflow-auto text-sm focus:outline-none",
          optionsClassName,
        )}
      >
        {filteredOptions.length > 0
          ? filteredOptions.map((option) =>
              renderOption ? renderOption(option) : <span key={option.id}>{displayValue(option)}</span>,
            )
          : query
            ? emptyNode || <NoResults />
            : null}
      </div>
    </div>
  );
};

export default SelectComboBox;

const NoResults = () => {
  const { t } = useTranslation();
  return (
    <div className={"flex flex-col items-center justify-center px-3 py-4 text-center text-sm text-quaternary"}>
      <HiOutlineDocumentMagnifyingGlass className={"mb-2 size-8"} />
      <p>{t("noResults.found")}</p>
      <p>{t("noResults.try")}</p>
    </div>
  );
};
